import { Collapse, IconButton, Box, Toolbar, Tooltip, Typography, Grid, Button, SelectChangeEvent, TextField, Select, MenuItem, Autocomplete } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { alpha } from '@mui/material/styles';
import React, { SyntheticEvent, useEffect, useRef, useState } from "react";
import { Picker } from "../../atoms";
import { HeadCell } from "./types";
import { FilterChips } from './tableFilterChips';
import * as constants from '../../../constants/_pickerValues';
import { useDispatch, useSelector } from "react-redux";
import { getProfileDataSelector } from "../../../store/auth/selectors";
import { getAccessControlPendingSelector, getAccessControlSelector } from "../../../store/dataSource/selectors";
import { IAccessControl } from "../../../store/dataSource/types";
import { fetchAccessControlRequest } from "../../../store/dataSource/actions";
import { getGroupPendingSelector, getGroupUsersSelector } from "../../../store/groups/selectors";
import { fetchGroupUsersRequest } from "../../../store/groups/actions"
import { IUser } from '../../../store/groups/types';
import LoopIcon from '@mui/icons-material/Refresh';
import { AddCircle } from "@mui/icons-material";
import { IDP_USERS_PRODUCTION } from "../../../constants/common";
import { fetchSpokeListSelector } from "../../../store/spokes/selectors";
import { getRequestStatusText, getRequestStatusPayloadText } from '../../shared/functions';

interface EnhancedTableToolbarProps {
    numSelected: number;
    title: string;
    headCells: HeadCell[];
    filterCells: string[];
    deleteAll: () => void;
    filterResults: (event: React.MouseEvent<HTMLButtonElement>, filterList: string[]) => void;
    currentFilters?: string[];
    refreshPage: () => void;
    personal?: boolean;
    openToolbar?: boolean;
    explorer?: string;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {

    const dispatch = useDispatch();
    //let filters: string[] = [];
    let userInit: IUser = { userName: '' };

    const {
        numSelected,
        title,
        filterCells,
        deleteAll,
        filterResults,
        refreshPage,
        currentFilters = [],
        openToolbar = false,
        explorer
    } = props;
    const [open, setOpen] = useState(openToolbar);
    const [filter, setFilter] = useState('');
    const [filterValue, setFilterValue] = useState('');
    const [filterList, setFilterList] = useState(currentFilters);
    const [updateFilters, setUpdateFilters] = useState(false);
    const [filterValueError, setFilterValueError] = useState('');
    const [filterMissing, setFilterMissing] = useState(false);
    const [spokes, setSpokes] = useState<any>();
    const applyFilterRef = useRef(null);

    const userProfile = useSelector(getProfileDataSelector);
    const accessControls = useSelector(getAccessControlSelector);
    const accessControlsPending = useSelector(getAccessControlPendingSelector);
    const idpUsersProduction = useSelector(getGroupUsersSelector(IDP_USERS_PRODUCTION));
    const idpUsersProductionPending = useSelector(getGroupPendingSelector(IDP_USERS_PRODUCTION));
    const spokeList = useSelector(fetchSpokeListSelector);

    useEffect(() => {
        if (spokeList && spokeList.length > 0) {
            setSpokes(spokeList.map(x => x.name));
        }
        else {
            setSpokes([]);
        }
    }, [spokeList])

    useEffect(() => {
        if (!idpUsersProductionPending && idpUsersProduction.length <= 0) {
            dispatch(fetchGroupUsersRequest(IDP_USERS_PRODUCTION));
        }
        if (!accessControlsPending && accessControls.length <= 0) {
            dispatch(fetchAccessControlRequest());
        }

    });

    useEffect(() => {
        setFilterMissing(false);
        if (filter === 'daysToExpiry') {
            setFilterValue('');
        }
    }, [filter]);

    useEffect(() => {
        setUpdateFilters(true);
    }, [filterList]);

    useEffect(() => {
        setFilterValueError('');
        if (filter &&
            filterValue &&
            (filter === 'layerType' || filter === 'owner' || filter === 'dataStewards' || filter === 'grantee' || filter === 'treatmentLevel' || filter === 'accessControl' || filter === 'username'
                || filter === 'status' || filter ==='dataSteward'
            )) {
                let f = filterList;
                f.push(filter + ": " + filterValue);
                setFilterList(f);
                setFilterValue('');
                setUpdateFilters(true);
        }

    }, [filterValue]);


    const handleFilterClick = () => {
        setFilter('');
        setFilterValue('');
        setOpen(!open);
    }

    const handleChangeFilter = (event: SelectChangeEvent) => {
        (event.target.name === 'value') ? setFilterValue(event.target.value) : setFilter(event.target.value);
        setUpdateFilters(false);
    };

    const treatmentLabelToValue = (treatment: string) => {
        switch (treatment) {
            case constants.TREATMENT_LABElS.low:
                return constants.TREATMENT_VALUES.low;
            case constants.TREATMENT_LABElS.medium:
                return constants.TREATMENT_VALUES.medium;
            case constants.TREATMENT_LABElS.high:
                return constants.TREATMENT_VALUES.high;
            case constants.TREATMENT_LABElS.veryHigh:
                return constants.TREATMENT_VALUES.veryHigh;
            case constants.TREATMENT_LABElS.access:
                return constants.TREATMENT_VALUES.access;
            default:
                return '';
        }
    }

    const treatmentValueToLabel = (treatment: string) => {
        switch (treatment) {
            case constants.TREATMENT_VALUES.low:
                return constants.TREATMENT_LABElS.low;
            case constants.TREATMENT_VALUES.medium:
                return constants.TREATMENT_LABElS.medium;
            case constants.TREATMENT_VALUES.high:
                return constants.TREATMENT_LABElS.high;
            case constants.TREATMENT_VALUES.veryHigh:
                return constants.TREATMENT_LABElS.veryHigh;
            case constants.TREATMENT_VALUES.access:
                return constants.TREATMENT_LABElS.access;
            default:
                return '';
        }
    }

    const handleChangeTreatmentValue = (event: SelectChangeEvent) => {
        setFilterValue(treatmentLabelToValue(event.target.value));
        setUpdateFilters(false);
    };

    const handleChangeStatusValue = (event: SelectChangeEvent) => {
        setFilterValue(getRequestStatusPayloadText(event.target.value));
        setUpdateFilters(false);
    };

    const handleChangeUserFilter = (event: SyntheticEvent<Element, Event>) => {
        if (event.currentTarget.textContent) {
            setFilterValue(event.currentTarget.textContent);
        }
    };

    const handleChangeDaysToExpiryValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterValueError('')
        let value = event.currentTarget.valueAsNumber;
        if (value > 0) {
            setFilterValue(value.toString());
        } else if (!isNaN(value)) {
            setFilterValueError('Value must be greater than 0');
        }
    };

    const handleChangeValue = (event: {}) => {
        const e = event as React.ChangeEvent<HTMLInputElement>;
        setFilterValue(e.currentTarget.value);
    }

    const addNewFilter = () => {
        if (filter && filterValue) {
            let f = filterList;
            f.push(filter + ": " + filterValue);
            setFilterList(f);
            setFilterValue('');
            setUpdateFilters(true);
        } else if (!filter) {
            setFilterMissing(true);
        } else {
            setFilterValueError('Value is required');
        }
    }


    // Used by button "Reset"
    const resetFilters = () => {
        setFilter('');
        setFilterValue('');
        setFilterList([]);
        setUpdateFilters(true);
        refreshPage();
    }

    // Used by icon loop
    const resetListFilter = (e: React.MouseEvent<HTMLButtonElement>) => {
        setFilter('');
        setFilterValue('');
        setFilterList([]);
        setUpdateFilters(true);
        filterResults(e, filterList);
        refreshPage();
    }

    // Remove from chip
    const removeFilter = (item: string) => {
        const newList = filterList.filter((itm: string) => itm !== item);
        setFilterList(newList);
        setUpdateFilters(true);
    };

    return (
        <>
            <Toolbar
                sx={{
                    pl: { sm: 2 },
                    pr: { xs: 1, sm: 1 },
                    ...(numSelected > 0 && {
                        bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                    }),
                }}
            >
                {numSelected > 0 ? (
                    <Typography
                        sx={{ flex: '1 1 100%', textAlign: 'left' }}
                        color="inherit"
                        variant="subtitle1"
                        component="div"
                    >
                        Rows : {numSelected} selected
                    </Typography>
                ) : (
                    <Typography
                        sx={{ flex: '1 1 100%', textAlign: 'left' }}
                        variant="h6"
                        id="tableTitle"
                        component="div"
                    >

                    </Typography>
                )}
                {numSelected > 0 ? (
                    <Tooltip title="Delete">
                        <IconButton onClick={deleteAll}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <>
                        <Tooltip title="Reset">
                            <IconButton onClick={resetListFilter} >
                                <LoopIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Filter list">
                            <IconButton onClick={handleFilterClick}>
                                <FilterListIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Toolbar>
            <Box sx={{ background: "#f5f5f5", padding: "0px 50px" }}>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Grid container spacing={2} direction="row" justifyContent="center" alignItems="center" marginTop={2}>
                        <Grid item xs={4}>
                            <Picker
                                name='filterFields'
                                label='Filter On'
                                options={filterCells}
                                value={filter}
                                onChange={handleChangeFilter}
                                error={filterMissing}
                                format='camelCase'
                            />
                        </Grid>
                        <Grid item xs={6}>
                            {filter === 'layerType' && title === 'Data Sources' &&
                                <Picker
                                    name='value'
                                    label='Value'
                                    options={[constants.EXTRACT_TYPES.mssql, constants.EXTRACT_TYPES.oracle, constants.EXTRACT_TYPES.postGres,
                                    constants.EXTRACT_TYPES.redShift, constants.EXTRACT_TYPES.s3Buckets, constants.EXTRACT_TYPES.windowsFileshare, constants.EXTRACT_TYPES.foundry]}
                                    value={filterValue}
                                    onChange={handleChangeFilter}
                                />
                            }
                            {filter === 'spokeName' &&
                                <Picker
                                    name='value'
                                    label='Value'
                                    options={spokes}
                                    value={filterValue}
                                    onChange={handleChangeFilter}
                                />
                            }
                            {filter === 'layerType' && title === 'Data Targets' &&
                                <Picker
                                    name='value'
                                    label='Value'
                                    options={[constants.LOAD_TYPES.adls, constants.LOAD_TYPES.mssql, constants.LOAD_TYPES.postGres,
                                    constants.LOAD_TYPES.redShift, constants.LOAD_TYPES.s3Buckets]}
                                    value={filterValue}
                                    onChange={handleChangeFilter}
                                />
                            }
                            {filter === 'treatmentLevel' && explorer !== 'overrides' &&
                                <Picker
                                    name='value'
                                    label='Value'
                                    options={[constants.TREATMENT_LABElS.low, constants.TREATMENT_LABElS.medium, constants.TREATMENT_LABElS.high, constants.TREATMENT_LABElS.veryHigh]}
                                    value={treatmentValueToLabel(filterValue)}
                                    onChange={handleChangeTreatmentValue}
                                />
                            }
                            {filter === 'treatmentLevel' && explorer === 'overrides' &&
                                <Picker
                                    name='value'
                                    label='Value'
                                    options={[constants.TREATMENT_LABElS.medium, constants.TREATMENT_LABElS.high, constants.TREATMENT_LABElS.access]}
                                    value={treatmentValueToLabel(filterValue)}
                                    onChange={handleChangeTreatmentValue}
                                />
                            }
                            {filter === 'status' &&
                                <Picker
                                    name='value'
                                    label='Value'
                                    options={Object.values(constants.REQUEST_STATUS)}
                                    value={getRequestStatusText(filterValue)}
                                    onChange={handleChangeStatusValue}
                                />
                            }
                            {filter === 'accessControl' &&
                                <Select
                                    name='value'
                                    value={filterValue.toLowerCase()}
                                    onChange={handleChangeFilter}
                                    fullWidth
                                >
                                    {
                                        accessControls.map((item: IAccessControl, index: number) => {
                                            return (
                                                <MenuItem key={index} value={item.groupName.toLowerCase()}>{item.groupName}</MenuItem>
                                            );
                                        })
                                    }
                                </Select>
                            }
                            {(filter === 'owner' || filter === 'grantee' || filter === 'dataStewards' || filter === 'dataSteward' ||filter === 'username') &&
                                <Autocomplete
                                    options={idpUsersProduction}
                                    getOptionLabel={(option) =>
                                        option === undefined ? " " : option.userName
                                    }
                                    onChange={handleChangeUserFilter}
                                    isOptionEqualToValue={(option, value) => option.userName === value.userName}
                                    renderInput={(params) => (
                                        <TextField {...params}
                                            name="value"
                                            required
                                            value={userInit}
                                        />
                                    )}
                                />
                            }
                            {(filter === 'daysToExpiry' && explorer === 'overrides') &&
                                <TextField
                                    label='Value'
                                    name='value'
                                    type='number'
                                    required={false}
                                    value={filterValue}
                                    onChange={handleChangeDaysToExpiryValue}
                                    fullWidth
                                    InputProps={{ inputProps: { min: 1 } }}
                                    helperText={filterValueError ? filterValueError : ''}
                                    error={!!(filterValueError)}
                                    disabled={false}
                                />
                            }
                            {(['layerType', 'owner', 'dataStewards', 'dataSteward', 'grantee', 'treatmentLevel', 'accessControl', 'spokeName', 'username', 'daysToExpiry', 'status'].indexOf(filter) < 0) &&
                                <TextField
                                    label='Value'
                                    name='value'
                                    required={false}
                                    value={(filter === 'owner' && props.personal) ? userProfile['mail'] : filterValue}
                                    onChange={handleChangeValue}
                                    fullWidth
                                    helperText={filterValueError ? filterValueError : ''}
                                    error={!!(filterValueError)}
                                    disabled={(filter === 'owner' && !!props.personal)}
                                />
                            }
                        </Grid>
                        <Grid item xs={1}> {(filter !== 'layerType' && filter !== 'owner' && filter !== 'dataStewards' && filter !== 'grantee' && filter !== 'treatmentLevel' && filter !== 'accessControl') &&
                            <IconButton aria-label="add" onClick={addNewFilter}>
                                <AddCircle />
                            </IconButton>}
                        </Grid>
                        <Grid item xs={13}>
                            <FilterChips filterList={filterList} update={updateFilters} removeFilter={removeFilter} />
                        </Grid>
                        <Grid item xs={13}>
                            <Box display="flex" justifyContent="flex-end" margin={2}>
                                <Button id="6b031c19-6fea-4240-b625-23ba91e946f5" variant="contained" onClick={resetFilters} >
                                    Reset
                                </Button>&nbsp;
                                <Button id="29548bab-eee1-4203-832a-8f4806398fd2" variant="contained" ref={applyFilterRef} onClick={(e) => filterResults(e, filterList)}>
                                    Apply Filters
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Collapse>
            </Box>
        </>
    );
};

export default EnhancedTableToolbar;
